import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { AlertBanner } from "@/components/admin/alert-banner";
import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { Kpi } from "@/components/admin/kpi";
import { AdminLineChart } from "@/components/admin/line-chart";
import { PageHead } from "@/components/admin/page-head";
import { Pagination } from "@/components/admin/pagination";
import { Segmented } from "@/components/admin/segmented";
import { Tag } from "@/components/admin/tag";
import { Can } from "@/components/auth/can";
import {
	COUNTRY_LABEL,
	DEVICE_LABEL,
	DimensionBars,
	INAPP_LABEL,
	REFERRER_LABEL,
	flagOf,
} from "@/components/admin/short-links/dimension-bars";
import { QrModal } from "@/components/admin/short-links/qr-modal";
import { useToast } from "@/components/ui/feedback";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { usePageTitle } from "@/hooks/use-page-title";
import { fmtN } from "@/lib/format";
import {
	CHANNEL_LABEL,
	KIND_LABEL,
	STATUS_LABEL,
	shortLinksApi,
} from "@/lib/short-links/short-links-api";
import {
	useInvalidateShortLinkCache,
	useShortLink,
	useShortLinkClicks,
	useShortLinkStats,
} from "@/lib/short-links/short-links-hooks";

const PERIODS = [
	{ value: "7", label: "7 j" },
	{ value: "30", label: "30 j" },
	{ value: "90", label: "90 j" },
];

const CLICKS_PAGE_SIZE = 50;

export function AdminShortLinkDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const toast = useToast();

	const [days, setDays] = useState("30");
	const [clicksPage, setClicksPage] = useState(1);
	const [includeBots, setIncludeBots] = useState(false);
	const [showQr, setShowQr] = useState(false);

	const { data: link, isLoading } = useShortLink(id);
	const { data: stats } = useShortLinkStats(id, Number(days));
	const { data: clicks } = useShortLinkClicks(id, clicksPage, CLICKS_PAGE_SIZE, includeBots);
	const invalidate = useInvalidateShortLinkCache();

	usePageTitle(link ? `Lien ${link.code}` : "Lien court");

	if (isLoading) {
		return <div className="muted center" style={{ padding: 48 }}>Chargement…</div>;
	}
	if (!link) {
		return (
			<>
				<PageHead title="Lien court introuvable" />
				<div className="empty">Ce lien n'existe pas ou a été supprimé.</div>
			</>
		);
	}

	const dims = stats?.dimensions ?? {};
	const totals = stats?.totals;
	const series = stats?.series ?? [];

	const copy = (text: string, message: string) => {
		void navigator.clipboard.writeText(text);
		toast.success(message);
	};

	const clickRows = clicks?.data ?? [];
	const clicksTotal = clicks?.total ?? 0;

	return (
		<>
			<PageHead
				title={link.code}
				sub={link.label ?? KIND_LABEL[link.kind]}
				actions={
					<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
						<AdminButton onClick={() => navigate("/liens-courts")}>Retour</AdminButton>
						<AdminButton onClick={() => copy(link.smsForm, "Lien copié pour SMS.")}>
							Copier pour SMS
						</AdminButton>
						{link.source === "manual" ? (
							<AdminButton onClick={() => setShowQr(true)}>QR code</AdminButton>
						) : null}
						<AdminButton onClick={() => void shortLinksApi.exportCsv(link.id, link.code)}>
							Exporter en CSV
						</AdminButton>
						<Can permission="MARKETING.CMS.WRITE">
							<AdminButton
								onClick={() => {
									invalidate
										.mutateAsync(link.id)
										.then(() => toast.success("Cache purgé."))
										.catch((e) =>
											toast.error(extractErrorMessage(e, "Purge impossible.")),
										);
								}}
							>
								Purger le cache
							</AdminButton>
						</Can>
					</div>
				}
			/>

			{totals && totals.nonHumanRate > 0.4 ? (
				<AlertBanner
					variant="warning"
					title={`${Math.round(totals.nonHumanRate * 100)} % des ouvertures ne sont pas humaines`}
					description="Aperçus de partage et pré-visites de passerelle. Le compteur mis en avant les exclut déjà — cette proportion élevée est normale sur un lien beaucoup partagé sur WhatsApp."
				/>
			) : null}

			<div className="info-row" style={{ marginBottom: 16 }}>
				<div>
					<div className="eyebrow">Destination</div>
					{link.targetRedacted ? (
						<div className="muted">
							Masquée — la destination d'un lien système contient un jeton d'accès à la
							commande.
						</div>
					) : (
						<div className="mono" style={{ wordBreak: "break-all", fontSize: 12.5 }}>
							{link.targetUrl}
						</div>
					)}
				</div>
			</div>

			<div className="grid-4" style={{ marginBottom: 16 }}>
				<div>
					<div className="eyebrow">Type</div>
					<div>{KIND_LABEL[link.kind]}</div>
				</div>
				<div>
					<div className="eyebrow">Canal</div>
					<div>{link.channel ? CHANNEL_LABEL[link.channel] : "—"}</div>
				</div>
				<div>
					<div className="eyebrow">Campagne</div>
					<div>{link.campaign ?? "—"}</div>
				</div>
				<div>
					<div className="eyebrow">Statut</div>
					<Tag variant={link.status === "active" ? "success" : "neutral"} noDot>
						{STATUS_LABEL[link.status]}
					</Tag>
				</div>
			</div>

			<div className="kpi-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
				<Kpi
					label="Clics humains"
					value={fmtN(totals?.humans ?? 0)}
					variant="accent"
					spark={series.map((s) => s.humans)}
				/>
				<Kpi label="Visiteurs uniques" value={fmtN(totals?.uniques ?? 0)} />
				<Kpi
					label="Envois"
					value={fmtN(stats?.dispatch.sent ?? 0)}
					foot={link.lastDispatchedAt ? `dernier : ${new Date(link.lastDispatchedAt).toLocaleDateString("fr-FR")}` : undefined}
				/>
				<Kpi
					label="Taux d'ouverture"
					value={
						stats?.dispatch.clickedRate === null || stats?.dispatch.clickedRate === undefined
							? "—"
							: `${Math.round(stats.dispatch.clickedRate * 100)} %`
					}
					variant="forest"
					/* Rapporté au nombre d'ENVOIS, pas au nombre de liens : un lien de
					   commande part six fois, le rapporter à « 1 » n'aurait aucun sens. */
					foot="visiteurs uniques rapportés aux envois"
				/>
			</div>

			<div className="stack" style={{ marginTop: 16 }}>
				<Card>
					<CardHead
						title="Ouvertures par jour"
						right={<Segmented options={PERIODS} value={days} onChange={setDays} />}
					/>
					<CardBody>
						<AdminLineChart
							data={series.map((s) => s.humans)}
							labels={series.map((s) => s.date.slice(5).replace("-", "/"))}
							color="var(--a-terracotta)"
							height={220}
						/>
					</CardBody>
				</Card>

				<div className="grid-3">
					<Card>
						<CardHead title="Navigateur" />
						<CardBody>
							<DimensionBars rows={dims.browser ?? []} max={6} />
						</CardBody>
					</Card>
					<Card>
						<CardHead title="Système" />
						<CardBody>
							<DimensionBars rows={dims.os ?? []} max={6} />
						</CardBody>
					</Card>
					<Card>
						<CardHead title="Appareil" />
						<CardBody>
							<DimensionBars rows={dims.device ?? []} labels={DEVICE_LABEL} max={6} />
						</CardBody>
					</Card>
				</div>

				<div className="grid-3">
					<Card>
						<CardHead title="Pays" />
						<CardBody>
							<DimensionBars
								rows={dims.country ?? []}
								labels={COUNTRY_LABEL}
								prefix={flagOf}
								max={6}
							/>
						</CardBody>
					</Card>
					<Card>
						<CardHead title="Provenance" />
						<CardBody>
							<DimensionBars rows={dims.inapp ?? []} labels={INAPP_LABEL} max={6} />
						</CardBody>
					</Card>
					<Card>
						<CardHead title="Référent" />
						<CardBody>
							<DimensionBars rows={dims.referrer ?? []} labels={REFERRER_LABEL} max={6} />
						</CardBody>
					</Card>
				</div>

				<Card>
					<CardHead
						title="Dernières ouvertures"
						right={
							<Segmented
								options={[
									{ value: "humans", label: "Humains" },
									{ value: "all", label: "Tout" },
								]}
								value={includeBots ? "all" : "humans"}
								onChange={(v) => {
									setIncludeBots(v === "all");
									setClicksPage(1);
								}}
							/>
						}
					/>
					<CardBody>
						{clickRows.length === 0 ? (
							<div className="empty">Aucune ouverture enregistrée.</div>
						) : (
							<>
								<div className="table-wrap">
									<table className="table">
										<thead>
											<tr>
												<th>Date</th>
												<th>Pays</th>
												<th>Appareil</th>
												<th>Navigateur</th>
												<th>Provenance</th>
												<th>Nature</th>
											</tr>
										</thead>
										<tbody>
											{clickRows.map((c) => (
												<tr key={c.id}>
													<td className="tn">
														{new Date(c.occurredAt).toLocaleString("fr-FR", {
															timeZone: "Africa/Douala",
														})}
													</td>
													<td>
														{c.countryCode ? (
															<>
																{flagOf(c.countryCode)}{" "}
																{COUNTRY_LABEL[c.countryCode] ?? c.countryCode}
															</>
														) : (
															<span className="muted">—</span>
														)}
													</td>
													<td>
														{DEVICE_LABEL[c.deviceType] ?? c.deviceType}
														{c.deviceBrand ? (
															<span className="muted"> · {c.deviceBrand}</span>
														) : null}
													</td>
													<td>{c.browser ?? <span className="muted">—</span>}</td>
													<td>
														{c.inApp ? (
															(INAPP_LABEL[c.inApp] ?? c.inApp)
														) : (
															<span className="muted">navigateur</span>
														)}
													</td>
													<td>
														{c.isBot ? (
															<Tag variant="warning" noDot>
																Robot
															</Tag>
														) : c.isPrefetch ? (
															<Tag variant="neutral" noDot>
																Pré-visite
															</Tag>
														) : (
															<Tag variant="success" noDot>
																Humain
															</Tag>
														)}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
								{clicksTotal > CLICKS_PAGE_SIZE ? (
									<Pagination
										from={(clicksPage - 1) * CLICKS_PAGE_SIZE + 1}
										to={Math.min(clicksPage * CLICKS_PAGE_SIZE, clicksTotal)}
										total={clicksTotal}
										current={clicksPage}
										totalPages={Math.ceil(clicksTotal / CLICKS_PAGE_SIZE)}
										onChange={setClicksPage}
									/>
								) : null}
							</>
						)}
					</CardBody>
				</Card>
			</div>

			{showQr ? <QrModal link={link} onClose={() => setShowQr(false)} /> : null}
		</>
	);
}
